import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Users, Globe, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { api, LINKS } from "@/lib/api";

const CLD = "https://res.cloudinary.com/dg5336ksj/image/upload/f_auto,q_auto/Arts-in-the-Park/";
const IMG = {
  hero: CLD + "-38.jpg",
  team: CLD + "-37.jpg",
  bookmark: CLD + "-36.jpg",
  progress: CLD + "-34.jpg",
  history: CLD + "-33.jpg",
  boys: CLD + "-32.jpg",
};

const PILLARS = [
  { n: "01", icon: BookOpen, title: "Literacy & Education", body: "Through our Pages of Progress initiative, we put books directly into the hands of children and families — building the foundation for lifelong learning. 10,000+ books distributed and counting." },
  { n: "02", icon: Globe, title: "Cultural Representation", body: "From the Layers of Black History Gala to the African Festival of Boston — we create spaces where Black heritage and diverse identities are celebrated and centered." },
  { n: "03", icon: Users, title: "Youth & Community", body: "The Men Raising Black Boys Conference, mentorship programs, and youth workshops invest directly in the next generation — giving young people the tools to lead." },
];

const PROGRAMS = [
  { img: IMG.progress, title: "Pages of Progress", body: "Our flagship literacy initiative — distributing thousands of books directly to children and families at community events across Greater Boston." },
  { img: IMG.history, title: "Layers of Black History", body: "An annual gala and year-round programming celebrating Black heritage, excellence, and cultural identity — creating space for joy and reflection." },
  { img: IMG.boys, title: "Men Raising Black Boys", body: "An annual conference investing in fatherhood, mentorship, and youth development — building the support systems young Black men need to thrive." },
];

export default function Home() {
  const [stats, setStats] = useState([]);
  const [albums, setAlbums] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/albums").then((r) => setAlbums(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-ink crosshatch radial-glow pt-[68px]" data-testid="home-hero">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center relative z-10">
          <div>
            <Reveal><span className="eyebrow">Ethos Foundation</span></Reveal>
            <Reveal delay={80}>
              <div className="mt-6 flex items-center gap-3 font-body-eth text-[12px] tracking-[0.2em] uppercase text-white/45">
                Ethos Foundation <span className="text-glow">·</span> EST 2021 <span className="text-glow">·</span> 501(c)(3)
              </div>
            </Reveal>
            <Reveal delay={140}>
              <h1 className="font-serif-eth font-light text-white mt-5 text-[46px] sm:text-6xl lg:text-[72px] leading-[1.02]">
                <em>Fostering unity</em>
                <br /> through community.
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="font-body-eth text-white/65 text-base lg:text-lg mt-7 max-w-xl leading-relaxed">
                Ethos Foundation builds vibrant, inclusive communities through literacy, cultural
                celebration, and youth leadership — one event, one book, one connection at a time.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/about_us" data-testid="hero-story-btn" className="btn-teal font-body-eth text-sm font-semibold px-7 py-3.5 inline-flex items-center gap-2">
                  Learn Our Story <ArrowRight size={16} />
                </Link>
                <Link to="/partnerships" data-testid="hero-involved-btn" className="font-body-eth text-sm font-semibold px-7 py-3.5 inline-flex items-center gap-2 text-white border border-white/25 hover:border-glow transition-colors" style={{ borderRadius: 2 }}>
                  Get Involved
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={200} className="hidden lg:block">
            <div className="relative">
              <div className="zoom-wrap" style={{ borderRadius: 2 }}>
                <img src={IMG.hero} alt="Ethos Foundation community" loading="lazy" className="w-full h-[440px] object-cover" style={{ borderRadius: 2 }} />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-ink2 border border-white/10 px-6 py-4" style={{ borderRadius: 2 }}>
                <div className="font-serif-eth text-3xl text-glow">10k+</div>
                <div className="font-body-eth text-[11px] uppercase tracking-[0.18em] text-white/50">Books Distributed</div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* hero stat strip */}
        <div className="border-t border-white/10 relative z-10">
          <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {[
              { v: "14,370+", l: "Community Members Reached" },
              { v: "10k+", l: "Books Distributed" },
              { v: "16", l: "Events since 2022" },
            ].map((s) => (
              <div key={s.l} className="py-7 sm:px-8 first:pl-0">
                <div className="font-serif-eth text-4xl text-white">{s.v}</div>
                <div className="font-body-eth text-[12px] tracking-wide text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION STRIP */}
      <section className="bg-sand" data-testid="home-mission">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
          <Reveal><span className="eyebrow dark mx-auto">Our Mission</span></Reveal>
          <Reveal delay={100}>
            <p className="font-serif-eth font-light text-ink text-[30px] sm:text-4xl lg:text-[46px] leading-[1.18] mt-7">
              To inspire learning, empower individuals, and build a brighter future by promoting{" "}
              <em>literacy</em>, celebrating <em>cultural identity</em>, and developing the{" "}
              <em>next generation of leaders.</em>
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="bg-cream" data-testid="home-about">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="zoom-wrap" style={{ borderRadius: 2 }}>
              <img src={IMG.team} alt="Ethos Foundation team" loading="lazy" className="w-full h-[460px] object-cover" style={{ borderRadius: 2 }} />
            </div>
            <div className="hidden sm:block absolute -bottom-8 -right-8 w-44 h-56 border-4 border-cream shadow-xl zoom-wrap" style={{ borderRadius: 2 }}>
              <img src={IMG.bookmark} alt="Child-made bookmark" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <div>
            <Reveal><span className="eyebrow dark">Who We Are</span></Reveal>
            <Reveal delay={80}>
              <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl leading-tight mt-5">
                Built on purpose,<br /> <em>powered by community.</em>
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="font-body-eth text-mute leading-relaxed mt-6">
                At Ethos Foundation, we believe that <strong className="text-ink font-semibold">vibrant, inclusive communities are the bedrock of a thriving society.</strong> We started as a small group of passionate individuals who recognized the power of community events to inspire, educate, and uplift.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <p className="font-body-eth text-mute leading-relaxed mt-4">
                Over the years, we've grown into a dynamic force for change — organizing events, distributing books, hosting conferences, and <strong className="text-ink font-semibold">building bridges across cultures, generations, and backgrounds.</strong>
              </p>
            </Reveal>
            <Reveal delay={260}>
              <p className="font-body-eth text-mute leading-relaxed mt-4">
                Our work is guided by a simple conviction: when people come together with intention, extraordinary things happen. Every event we host, every book we give, every conversation we spark is a step toward the world we're building together.
              </p>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                {["501(c)(3) Nonprofit", "Boston, MA", "Est. 2021", "10,000+ Books Distributed"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2 font-body-eth text-sm text-ink">
                    <Check size={15} className="text-teal" /> {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="bg-ink crosshatch" data-testid="home-pillars">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal><span className="eyebrow">What We Stand For</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-white text-4xl lg:text-5xl mt-5">
              Three pillars. <em>One mission.</em>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 120}>
                <div className="group eth-card bg-ink2 border border-white/10 p-8 h-full">
                  <div className="flex items-center justify-between">
                    <p.icon size={26} className="text-glow" />
                    <span className="font-serif-eth text-4xl text-white/15">{p.n}</span>
                  </div>
                  <h3 className="font-serif-eth text-2xl text-white mt-6 underline-grow inline-block pb-1">{p.title}</h3>
                  <p className="font-body-eth text-white/55 text-sm leading-relaxed mt-4">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="bg-cream" data-testid="home-gallery-preview">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Reveal><span className="eyebrow dark">Community Gallery</span></Reveal>
              <Reveal delay={80}>
                <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl mt-5">
                  Every moment <em>captured.</em>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={120}>
              <Link to="/media" data-testid="gallery-preview-link" className="btn-navy font-body-eth text-sm font-semibold px-6 py-3 inline-flex items-center gap-2">
                View Full Gallery <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-10">
            {albums.map((a, i) => (
              <Reveal key={a.id} delay={i * 90}>
                <Link to="/media" className="group block zoom-wrap relative overflow-hidden" style={{ borderRadius: 2, aspectRatio: "3 / 4" }}>
                  <img src={a.cover} alt={a.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/25 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="font-body-eth text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 text-white inline-block" style={{ background: a.color, borderRadius: 2 }}>{a.tag}</span>
                    <div className="font-serif-eth text-white text-lg leading-tight mt-2">{a.name}</div>
                    <div className="font-body-eth text-white/55 text-xs mt-1">{a.count} photos</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT COUNTERS */}
      <section className="bg-ink2 crosshatch" data-testid="home-impact">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal><span className="eyebrow">2024 Impact</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-white text-4xl lg:text-5xl mt-5">
              Our mission <em>measured.</em>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 100}>
                <div className="border-l border-white/15 pl-5">
                  <div className="font-serif-eth text-glow text-5xl lg:text-6xl">
                    <Counter value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="font-body-eth text-white text-sm mt-3">{s.label}</div>
                  <div className="font-body-eth text-white/45 text-xs">{s.sub}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-sand" data-testid="home-programs">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal><span className="eyebrow dark">What We Do</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl mt-5">
              Programs that <em>move people.</em>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.title} delay={i * 120}>
                <div className="group eth-card bg-white border border-[var(--line)] overflow-hidden h-full">
                  <div className="zoom-wrap h-52">
                    <img src={p.img} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif-eth text-2xl text-ink underline-grow inline-block pb-1">{p.title}</h3>
                    <p className="font-body-eth text-mute text-sm leading-relaxed mt-4">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GET INVOLVED STRIP */}
      <section className="bg-ink crosshatch radial-glow" data-testid="home-cta">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center relative z-10">
          <Reveal><span className="eyebrow mx-auto">Join The Mission</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-white text-4xl lg:text-[52px] mt-6 leading-tight">
              Ready to build <em>something together?</em>
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="font-body-eth text-white/60 max-w-xl mx-auto mt-6 leading-relaxed">
              Whether you want to volunteer, sponsor an event, donate books, or become a corporate
              partner — there's a place for you in the Ethos community.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-9 flex flex-wrap gap-4 justify-center">
              <Link to="/donate" className="btn-gold font-body-eth text-sm font-semibold px-8 py-3.5">Donate Now</Link>
              <a href={`mailto:${LINKS.email}`} className="btn-teal font-body-eth text-sm font-semibold px-8 py-3.5">Get In Touch</a>
              <a href={LINKS.website} target="_blank" rel="noreferrer" className="font-body-eth text-sm font-semibold px-8 py-3.5 text-white border border-white/25 hover:border-glow transition-colors" style={{ borderRadius: 2 }}>Visit Our Website</a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
